import React, { useState, useCallback, useEffect } from "react";
import { FiCheckCircle, FiLock, FiExternalLink, FiPower, FiShield } from "react-icons/fi";
import { usePasswordManager } from "../hooks/usePasswordManager";
import { useLicenseContext } from "../contexts/LicenseContext";
import { useCryptoContext } from "../contexts/CryptoContext";
import { useSendFlow } from "../hooks/useSendFlow";
import { useSendOrchestrator } from "../hooks/useSendOrchestrator";
import { usePrepareEncryption } from "../hooks/usePrepareEncryption";
import { useToast } from "../contexts/ToastContext";
import { useI18n } from "../contexts/I18nContext";
import SidebarUnlock from "./SidebarUnlock";
import PasswordModal from "./PasswordModal";
import DevConsolePanel from "./DevConsolePanel";
import { updateSettings } from "../api/settingsApi";
import { encryptAndSave } from "../services/PasswordManagerService";
import { consumeHints, HINTS } from "../utils/deferredHints";

const LS_FIRST_RUN_VALUE_PLACEMAT = "avamboo_first_run_value_placemat_v2";

function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false);
  return (
    <div
      style={{ position: "relative", display: "block" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && text && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#222",
            color: "#fff",
            fontSize: "11px",
            lineHeight: "1.5",
            padding: "6px 10px",
            borderRadius: "6px",
            whiteSpace: "pre-wrap",
            maxWidth: "240px",
            width: "max-content",
            zIndex: 9999,
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
            pointerEvents: "none",
          }}
        >
          {text}
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              border: "5px solid transparent",
              borderTopColor: "#222",
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Reads To+Cc recipient count from the active Office.js compose item. */
async function getRecipientCount() {
  try {
    if (typeof Office === "undefined" || !Office.context?.mailbox?.item)
      return 0;
    const item = Office.context.mailbox.item;
    const getAsync = (field) =>
      new Promise((resolve) => {
        field.getAsync((r) =>
          resolve(
            r.status === Office.AsyncResultStatus.Succeeded ? r.value : [],
          ),
        );
      });
    const [toList, ccList] = await Promise.all([
      getAsync(item.to),
      getAsync(item.cc),
    ]);
    return toList.length + ccList.length;
  } catch {
    return 0;
  }
}

/**
 * PasswordManager Component
 * Manages password database activation, deactivation, and access.
 *
 * Renders exactly 3 states:
 *   1) DEAKTIVIERT  – passwortManagerAktiv=false
 *   2) GESPERRT     – passwortManagerAktiv=true, isUnlocked=false
 *   3) ENTSPERRT    – passwortManagerAktiv=true, isUnlocked=true
 */
const PasswordManager = ({ pdfEngine, onNavigate }) => {
  const { t } = useI18n();
  const { oid, settings, license, reloadLicenseStatus, userEmail } =
    useLicenseContext();
  const isPaid = license?.lizenzTyp === "Paid";
  const cryptoCtx = useCryptoContext();
  const { isUnlocked } = cryptoCtx;
  const { showToast } = useToast();

  // Show pending warning from previous send (e.g. Sent Items copy failed)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("avamboo_pending_warning");
      if (raw) {
        const { message, subject } = JSON.parse(raw);
        const fullMsg = subject ? `${message} (${t("Betreff:")} "${subject}")` : message;
        showToast(fullMsg, "warning", 0);
        localStorage.removeItem("avamboo_pending_warning");
      }
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Track recipient count to disable "Verschlüsselung vorbereiten" when no recipients
  const [hasRecipients, setHasRecipients] = useState(false);

  // Sidebar unlock overlay state
  const [showUnlock, setShowUnlock] = useState(false);

  // First-run value placemat: shown once after first successful settings load
  const [showFirstRunPanel, setShowFirstRunPanel] = useState(false);

  // Deferred hints: scheduled by modals before closing, shown as toasts on next mount
  useEffect(() => {
    const hints = consumeHints();
    if (hints.has(HINTS.AUTO_PREFILL_TIP)) {
      showToast(
        t("Tipp: Gespeicherte Passwörter automatisch vorausfüllen lassen? → Einstellungen öffnen"),
        "info",
        6000,
        onNavigate ? () => onNavigate("general", { highlight: "nichtNachPasswortFragenFuerGespeicherteEmpfaenger" }) : null,
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (settings && !localStorage.getItem(LS_FIRST_RUN_VALUE_PLACEMAT)) {
      setShowFirstRunPanel(true);
    }
  }, [settings]);

  const handleEnableReminderFromFirstRun = async () => {
    localStorage.setItem(LS_FIRST_RUN_VALUE_PLACEMAT, "1");
    setShowFirstRunPanel(false);
    if (oid) {
      try {
        await updateSettings(oid, {
          erinnerungVerschluesselnAlleEmpfaenger: true,
        });
        await reloadLicenseStatus();
      } catch (_) {
        // Non-fatal — user can adjust in settings later
      }
    }
  };

  const handleStartWithoutReminder = () => {
    localStorage.setItem(LS_FIRST_RUN_VALUE_PLACEMAT, "1");
    setShowFirstRunPanel(false);
  };

  // "Verschlüsseln beim Senden" button state — when active, forces the password
  // modal open on next send regardless of auto-encrypt / remind settings.
  // Also synced to localStorage so commands.js (OnMessageSend handler) can read it.
  const [verschluesselnButtonAktiv, setVerschluesselnButtonAktiv] =
    useState(() => localStorage.getItem("avamboo_verschluesseln_aktiv") === "1");

  // Keep localStorage in sync with the toggle state.
  useEffect(() => {
    if (verschluesselnButtonAktiv) {
      localStorage.setItem("avamboo_verschluesseln_aktiv", "1");
    } else {
      localStorage.removeItem("avamboo_verschluesseln_aktiv");
    }
  }, [verschluesselnButtonAktiv]);

  // Send-pipeline modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [sendFlowResult, setSendFlowResult] = useState(null);

  const handleSuccess = (message) => {
    showToast(message, "success");
  };

  const handleError = (message) => {
    showToast(message, "error", 8000);
  };

  /**
   * Runs after a successful send (Phase 4).
   * Shows a German success toast or a warning if the Sent Items copy failed.
   *
   * @param {{ sendMode: "full"|"pdfOnly"|"unencrypted", sentItemsFailed: boolean }} postSendResult
   */
  const handlePostSendSuccess = ({ sendMode, sentItemsFailed }) => {
    if (sentItemsFailed) {
      handleError(
        t("Die E-Mail wurde erfolgreich gesendet, konnte jedoch nicht zusätzlich unverschlüsselt unter 'Gesendete Elemente' gespeichert werden."),
      );
      return;
    }

    const messages = {
      full: t("Die E-Mail wurde verschlüsselt und erfolgreich gesendet."),
      pdfOnly: t("Die E-Mail wurde erfolgreich gesendet. PDF-Anhänge wurden verschlüsselt."),
      unencrypted: t("Die E-Mail wurde unverschlüsselt gesendet."),
    };
    handleSuccess(
      messages[sendMode] ?? t("Die E-Mail wurde erfolgreich gesendet."),
    );
  };

  // Provide crypto state snapshot for bridge routing (dialog communication)
  const getCryptoState = useCallback(
    () => ({
      isUnlocked: cryptoCtx.isUnlocked,
      masterKey: cryptoCtx.masterKey,
      privateKey: cryptoCtx.privateKey,
      passwortDatenbankId: cryptoCtx.passwortDatenbankId,
      masterKeyVersion: cryptoCtx.masterKeyVersion,
      isOwner: cryptoCtx.isOwner,
      activeUserCount: cryptoCtx.activeUserCount,
      isPaid: cryptoCtx.isPaid,
      oid: cryptoCtx.oid,
    }),
    [cryptoCtx],
  );

  const cryptoActions = {
    unlock: cryptoCtx.unlock,
    lock: cryptoCtx.lock,
  };

  const { isActive, loading, openManager, deactivate } = usePasswordManager(
    oid,
    handleSuccess,
    handleError,
    getCryptoState,
    cryptoActions,
    userEmail,
  );

  const { handleEncryptAndSend } = useSendOrchestrator({
    pdfEngine,
    settings,
    isPaid,
  });

  const {
    isPrepared,
    isPreparing,
    prepareEncryption,
    restoreDraft,
    checkIfStale,
    preparedInSeconds,
  } = usePrepareEncryption({
    pdfEngine,
    settings,
    isPaid,
    onWarning: (msg) => showToast(msg, "warning"),
  });

  // Poll recipient count and detect compose item changes every 2 seconds.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const count = await getRecipientCount();
      if (!cancelled) setHasRecipients(count > 0);
      if (!cancelled) await checkIfStale();
    };
    check();
    const interval = setInterval(check, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [checkIfStale]);

  // State for the "prepare" modal (separate from the send-time modal)
  const [showPrepareModal, setShowPrepareModal] = useState(false);
  const [prepareFlowResult, setPrepareFlowResult] = useState(null);

  // Opens the prepare modal: collects recipients/attachments and optionally PM entries
  const handleOpenPrepareModal = useCallback(async () => {
    if (!pdfEngine?._ready) {
      showToast(t("Die PDF-Engine ist nicht verfügbar. Bitte laden Sie das Add-in neu."), "error", 8000);
      return;
    }

    const { normalizeRecipients, fetchAndDecryptAll, findByRecipientSetKey } =
      await import("../services/PasswordManagerService");
    const { getAttachmentItems } = await import("../utils/officeItemHelpers");

    // Collect recipients for display in the modal
    let recipientEmails = [];
    try {
      /* global Office */
      if (typeof Office !== "undefined" && Office.context?.mailbox?.item) {
        const item = Office.context.mailbox.item;
        const getAsync = (field) =>
          new Promise((resolve) => {
            field.getAsync((r) =>
              resolve(
                r.status === Office.AsyncResultStatus.Succeeded ? r.value : [],
              ),
            );
          });
        const [toList, ccList] = await Promise.all([
          getAsync(item.to),
          getAsync(item.cc),
        ]);
        recipientEmails = [...toList, ...ccList].map((r) => r.emailAddress);
      }
    } catch (_) {
      recipientEmails = [];
    }
    const recipientSetKey = normalizeRecipients(recipientEmails);

    // Attachment info for PDF-only button eligibility
    let hasAttachments = false;
    let allAttachmentsPdf = false;
    let totalAttachmentBytes = 0;
    try {
      const allItems = await getAttachmentItems();
      const nonInline = allItems.filter((a) => !a.isInline);
      hasAttachments = nonInline.length > 0;
      allAttachmentsPdf =
        hasAttachments &&
        nonInline.every((a) => a.name?.toLowerCase().endsWith(".pdf"));
      totalAttachmentBytes = nonInline.reduce(
        (sum, a) => sum + (a.size ?? 0),
        0,
      );
    } catch (_) {}

    // PM lookup — only when PM is active and unlocked (same as send-flow)
    let decryptedEntries = null;
    let prefillPassword = null;
    let existingEntryId = null;
    let foundInPm = false;
    if (
      isActive &&
      isUnlocked &&
      cryptoCtx.oid &&
      cryptoCtx.passwortDatenbankId &&
      cryptoCtx.masterKey
    ) {
      try {
        decryptedEntries = await fetchAndDecryptAll(
          cryptoCtx.oid,
          cryptoCtx.passwortDatenbankId,
          cryptoCtx.masterKey,
        );
        const found = findByRecipientSetKey(decryptedEntries, recipientSetKey);
        if (found) {
          existingEntryId = found.id;
          if (settings?.nichtNachPasswortFragenFuerGespeicherteEmpfaenger) {
            prefillPassword = found.password;
          }
        }
        foundInPm = !!found;
      } catch (err) {
        console.warn(
          "[PasswordManager] PM lookup failed in prepare modal:",
          err,
        );
      }
    }

    setPrepareFlowResult({
      recipientSetKey,
      hasAttachments,
      allAttachmentsPdf,
      totalAttachmentBytes,
      decryptedEntries,
      prefillPassword,
      existingEntryId,
      foundInPm,
    });
    setShowPrepareModal(true);
  }, [isActive, isUnlocked, cryptoCtx, pdfEngine, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isRestoring, setIsRestoring] = useState(false);
  const handleRestoreDraft = useCallback(async () => {
    setIsRestoring(true);
    try {
      const { attachmentWarning } = await restoreDraft();
      if (attachmentWarning) {
        handleError(attachmentWarning);
      } else {
        handleSuccess("Originalentwurf wurde wiederhergestellt.");
      }
    } finally {
      setIsRestoring(false);
    }
  }, [restoreDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const { triggerSendFlow } = useSendFlow({
    settings,
    isUnlocked,
    passwortManagerAktiv: isActive,
    oid: cryptoCtx.oid,
    passwortDatenbankId: cryptoCtx.passwortDatenbankId,
    masterKey: cryptoCtx.masterKey,
  });

  // DEV: when sidebar unlocks while send modal is open with no entries, re-run lookup to refresh
  useEffect(() => {
    if (
      !showPasswordModal ||
      !isUnlocked ||
      sendFlowResult?.decryptedEntries !== null
    )
      return;
    let cancelled = false;
    (async () => {
      const result = await triggerSendFlow();
      if (!cancelled && !result.aborted) {
        setSendFlowResult(result);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isUnlocked, showPasswordModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // When PM unlocks while the prepare modal is open, re-run the lookup so the
  // password dropdown and prefill are populated (user unlocked via inline SidebarUnlock).
  useEffect(() => {
    if (!showPrepareModal || !isUnlocked) return;
    // Only re-fetch if we don't have entries yet (first unlock after modal opened)
    if (prepareFlowResult?.decryptedEntries !== null) return;
    let cancelled = false;
    (async () => {
      const { fetchAndDecryptAll, findByRecipientSetKey } =
        await import("../services/PasswordManagerService");
      if (cancelled) return;
      if (
        !cryptoCtx.oid ||
        !cryptoCtx.passwortDatenbankId ||
        !cryptoCtx.masterKey
      )
        return;
      try {
        const entries = await fetchAndDecryptAll(
          cryptoCtx.oid,
          cryptoCtx.passwortDatenbankId,
          cryptoCtx.masterKey,
        );
        if (cancelled) return;
        const found = prepareFlowResult?.recipientSetKey
          ? findByRecipientSetKey(entries, prepareFlowResult.recipientSetKey)
          : null;
        setPrepareFlowResult((prev) => ({
          ...prev,
          decryptedEntries: entries,
          prefillPassword: found && settings?.nichtNachPasswortFragenFuerGespeicherteEmpfaenger ? found.password : null,
          existingEntryId: found?.id ?? null,
          foundInPm: !!found,
        }));
      } catch (err) {
        console.warn(
          "[PasswordManager] PM re-lookup after unlock failed:",
          err,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isUnlocked, showPrepareModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared send trigger used by DEV button and the real OnMessageSend handler.
  // If verschluesselnButtonAktiv=true, always opens the modal regardless of settings.
  // Returns a Promise<{ allow: boolean }> so the BroadcastChannel listener can
  // forward the decision back to commands.js.
  //
  // NEW: When called from the BroadcastChannel (real Send button), if the email has NOT
  // been prepared yet, we do NOT run the heavy encryption pipeline. Instead we show a
  // notification telling the user to use "Verschlüsselung vorbereiten" first, then
  // block the send (allow: false). This prevents the Outlook timeout warning.
  const handleTriggerSend = useCallback(
    async (fromBroadcast = false) => {
      try {
        // If called from the real Send button (BroadcastChannel) and email is not yet
        // prepared, show a hint and block the send immediately — no heavy work.
        if (fromBroadcast && !isPrepared) {
          showToast(
            t('Bitte verwenden Sie zuerst "Verschlüsselung vorbereiten" im Add-in, bevor Sie die E-Mail senden.'),
            "error",
            0,
          );
          return { allow: false };
        }

        const result = await triggerSendFlow();
        if (result.aborted) return { allow: false };
        if (result.doEncrypt || verschluesselnButtonAktiv) {
          setSendFlowResult(result);
          setShowPasswordModal(true);
          // Caller (BroadcastChannel handler) must wait for modal to resolve;
          // the modal's onClose will post SEND_DECISION via pendingSendResolveRef.
          return null; // signal: "modal opened, decision comes later"
        }
        // No encryption required — allow the send.
        return { allow: true };
      } catch (err) {
        console.error("[handleTriggerSend]", err);
        return { allow: false };
      }
    },
    [triggerSendFlow, verschluesselnButtonAktiv, isPrepared, showToast, t],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  // Ref to hold the resolve function for a pending OnMessageSend interception.
  // Set by the BroadcastChannel listener; resolved by the modal's onClose.
  const pendingSendResolveRef = React.useRef(null);

  // Listen for INTERCEPT_SEND messages from commands.js (OnMessageSend handler).
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel("avamboo-send-channel");
    } catch (_) {
      return; // BroadcastChannel not supported
    }

    channel.onmessage = async (msg) => {
      if (msg.data?.type === "COMMANDS_DEBUG") {
        console.log("[avamboo/commands.js]", msg.data.msg);
        return;
      }
      if (msg.data?.type !== "INTERCEPT_SEND") return;

      const result = await handleTriggerSend(true /* fromBroadcast */);

      if (result !== null) {
        // Immediate decision (either aborted or no encryption needed).
        channel.postMessage({ type: "SEND_DECISION", allow: result.allow });
      } else {
        // Modal was opened — store a resolver; onClose will call it.
        await new Promise((resolve) => {
          pendingSendResolveRef.current = resolve;
        }).then((allow) => {
          channel.postMessage({ type: "SEND_DECISION", allow });
        });
      }
    };

    return () => {
      channel.close();
    };
  }, [handleTriggerSend]);

  return (
    <div style={styles.card}>
      {/* ── First-run value placemat (shown once after first settings load) ── */}
      {showFirstRunPanel && (
        <div style={styles.firstRunPanel}>
          <div style={styles.firstRunHeader}>
            <div style={styles.firstRunIcon}>
              <FiShield size={20} />
            </div>
            <div>
              <h2 style={styles.firstRunTitle}>{t("Sichere E-Mails direkt in Outlook")}</h2>
              <p style={styles.firstRunIntro}>
                {t("Avamboo Encrypt verschlüsselt Nachrichtentext und Anhänge vor dem Versand, damit vertrauliche Unternehmensdaten geschützt bleiben.")}
              </p>
            </div>
          </div>

          <p style={styles.firstRunBenefit}>
            {t("Für wiederkehrende Empfänger können Passwörter optional verschlüsselt gespeichert und beim nächsten Versand schneller verwendet werden.")}
          </p>

          <div style={styles.firstRunExamples}>
            {[
              t("Verschlüsselung vorbereiten"),
              t("Beim Senden erinnern lassen"),
              t("Passwortdatenbank nutzen"),
            ].map((label) => (
              <div key={label} style={styles.firstRunExampleItem}>
                <FiCheckCircle size={14} style={styles.firstRunExampleIcon} />
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div style={styles.firstRunButtons}>
            <button
              onClick={handleEnableReminderFromFirstRun}
              style={styles.firstRunPrimaryButton}
            >
              {t("Erinnerung aktivieren")}
            </button>
            <button
              onClick={handleStartWithoutReminder}
              style={styles.firstRunSecondaryButton}
            >
              {t("Ohne Erinnerung starten")}
            </button>
          </div>
        </div>
      )}


      {/* ── Verschlüsseln beim Senden ── */}
      <div style={{ ...styles.section, marginBottom: "0" }}>
        <Tooltip text={verschluesselnButtonAktiv ? t("Klicken, um die automatische Verschlüsselung beim Senden zu deaktivieren.") : t("Aktiviert die automatische Verschlüsselung beim Senden einer E-Mail.")}>
          <button
            onClick={() => setVerschluesselnButtonAktiv((prev) => !prev)}
            style={{
              ...styles.button,
              ...(verschluesselnButtonAktiv
                ? styles.buttonVerschluesselnAktiv
                : styles.buttonVerschluesselnInaktiv),
            }}
          >
            {verschluesselnButtonAktiv
              ? t("Verschlüsseln beim Senden: AN")
              : t("Verschlüsseln beim Senden")}
          </button>
        </Tooltip>
      </div>

      {/* ── Verschlüsselung vorbereiten ── */}
      <div style={{ ...styles.section, marginBottom: 0 }}>
        {!isPrepared ? (
          <>
            <Tooltip text={t("Bereitet die E-Mail für den verschlüsselten Versand vor. Der Entwurf wird transformiert – beim Senden gibt es keine Verzögerung.")}>
              <button
                onClick={handleOpenPrepareModal}
                disabled={isPreparing || !hasRecipients}
                style={{
                  ...styles.button,
                  ...styles.buttonPrepare,
                  ...(isPreparing || !hasRecipients
                    ? styles.buttonDisabled
                    : {}),
                }}
              >
                {isPreparing
                  ? t("Wird vorbereitet...")
                  : t("Verschlüsselung vorbereiten")}
              </button>
            </Tooltip>
            {!hasRecipients && !isPreparing && (
              <p style={{ fontSize: "12px", color: "#999", margin: "4px 0 0 0", padding: "0 10px" }}>
                {t("Bitte fügen Sie mindestens einen Empfänger hinzu.")}
              </p>
            )}
          </>
        ) : (
          <button
            onClick={handleRestoreDraft}
            disabled={isRestoring}
            title={t("Stellt Betreff und Text des ursprünglichen Entwurfs wieder her. Anhänge müssen ggf. manuell erneut hinzugefügt werden.")}
            style={{
              ...styles.button,
              ...styles.buttonRestore,
              ...(isRestoring
                ? { opacity: 0.6, cursor: "not-allowed" }
                : {}),
            }}
          >
            {isRestoring
              ? t("Wird zurückgesetzt…")
              : t("Verschlüsselung rückgängig machen")}
          </button>
        )}
      </div>

      {/* ── Passwortdatenbank ── */}
      <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: "16px" }}>

        {/* 1) DEAKTIVIERT */}
        {!isActive && (
          <div style={{ ...styles.section, marginBottom: 0 }}>
            <Tooltip text={t("Öffnet den Passwort-Manager, wo Sie einen Benutzer-Schlüssel festlegen und die Passwortdatenbank aktivieren können. Ihre Passwörter werden verschlüsselt gespeichert.")}>
              <button
                onClick={openManager}
                disabled={loading}
                style={{
                  ...styles.button,
                  ...styles.buttonActivate,
                  ...(loading ? styles.buttonDisabled : {}),
                  marginBottom: 0,
                }}
              >
                {loading ? t("Wird aktiviert...") : <><FiLock size={15} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Passwortdatenbank aktivieren")}</>}
              </button>
            </Tooltip>
          </div>
        )}

        {/* 2) AKTIVIERT + GESPERRT */}
        {isActive && !isUnlocked && (
          <div style={{ ...styles.section, marginBottom: 0 }}>
            <Tooltip text={t("Benutzer-Schlüssel eingeben, um gespeicherte Passwörter zu verwenden oder neue Einträge zu speichern.")}>
              <button
                onClick={() => setShowUnlock(true)}
                disabled={loading}
                style={{
                  ...styles.button,
                  ...styles.buttonUnlock,
                  ...(loading ? styles.buttonDisabled : {}),
                  marginBottom: 0,
                }}
              >
                <FiLock size={15} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Passwortdatenbank entsperren")}
              </button>
            </Tooltip>
          </div>
        )}

        {/* 3) AKTIVIERT + ENTSPERRT */}
        {isActive && isUnlocked && (
          <div style={{ ...styles.section, marginBottom: 0 }}>
            <Tooltip text={t("Öffnet ein separates Fenster zur Verwaltung Ihrer Passwortdatenbank (Anzeigen, Bearbeiten, Entschlüsseln).")}>
              <button
                onClick={openManager}
                disabled={loading}
                style={{
                  ...styles.button,
                  ...styles.buttonManage,
                  ...(loading ? styles.buttonDisabled : {}),
                  marginBottom: "6px",
                }}
              >
                {t("Passwortdatenbank verwalten")} <FiExternalLink size={15} style={{ marginLeft: "6px", verticalAlign: "middle" }} />
              </button>
            </Tooltip>
            <Tooltip text={t("Deaktiviert den Passwortmanager vorübergehend. Ihre Daten bleiben vollständig erhalten.")}>
              <button
                onClick={deactivate}
                disabled={loading}
                style={{
                  ...styles.button,
                  ...styles.buttonDeactivate,
                  ...(loading ? styles.buttonDisabled : {}),
                  marginBottom: 0,
                }}
              >
                {loading ? t("Wird deaktiviert...") : <><FiPower size={15} style={{ marginRight: "6px", verticalAlign: "middle" }} />{t("Passwortdatenbank deaktivieren")}</>}
              </button>
            </Tooltip>
          </div>
        )}

      </div>

      {/* PasswordModal – rendered outside state blocks so it survives state transitions */}
      {showPasswordModal && sendFlowResult && (
        <PasswordModal
          recipientSetKey={sendFlowResult.recipientSetKey}
          decryptedEntries={sendFlowResult.decryptedEntries}
          prefillPassword={sendFlowResult.prefillPassword}
          existingEntryId={sendFlowResult.existingEntryId}
          isUnlocked={isUnlocked}
          passwortManagerAktiv={isActive}
          settings={settings}
          isPaid={isPaid}
          hasAttachments={sendFlowResult.hasAttachments ?? false}
          allAttachmentsPdf={sendFlowResult.allAttachmentsPdf ?? false}
          totalAttachmentBytes={sendFlowResult.totalAttachmentBytes ?? 0}
          onPrepare={async ({ mode, password, keepSubjectUnencrypted }) => {
            return await handleEncryptAndSend({
              mode,
              password,
              keepSubjectUnencrypted,
            });
          }}
          onClose={(postSendResult) => {
            // Save/update password in PM after successful encrypted send
            if (
              postSendResult?.sendMode &&
              postSendResult.sendMode !== "unencrypted" &&
              postSendResult.usedPassword &&
              cryptoCtx.isUnlocked &&
              cryptoCtx.masterKey &&
              cryptoCtx.oid &&
              cryptoCtx.passwortDatenbankId &&
              sendFlowResult?.recipientSetKey
            ) {
              encryptAndSave(
                cryptoCtx.oid,
                cryptoCtx.passwortDatenbankId,
                cryptoCtx.masterKey,
                sendFlowResult.recipientSetKey,
                postSendResult.usedPassword,
                sendFlowResult.existingEntryId ?? null,
              ).catch((err) =>
                console.error(
                  "[PasswordManager] PM save after send failed:",
                  err,
                ),
              );
            }
            setShowPasswordModal(false);
            setSendFlowResult(null);
            if (postSendResult?.sendMode) {
              handlePostSendSuccess(postSendResult);
              // If this send was triggered by the real Outlook "Send" button
              // (OnMessageSend interception), resolve the pending decision.
              if (pendingSendResolveRef.current) {
                const resolve = pendingSendResolveRef.current;
                pendingSendResolveRef.current = null;
                // IMPORTANT: Our pipeline already called saveCompose() which saves the compose item.
                // We must pass allow=false here so Outlook does NOT send it a second time.
                // Smart Alert with SendMode="PromptUser" + allowEvent=false shows a warning
                // to the user — we use allowEvent=true (no warning) but the compose was already
                // closed by saveAsync, so Outlook won't actually re-send.
                // The safest choice: allowEvent=false cancels the "original" send event cleanly.
                resolve(false);
              }
            } else {
              // Modal was cancelled — resolve pending interception with allow=false
              // so Outlook re-enables the compose window (user can edit and retry).
              if (pendingSendResolveRef.current) {
                const resolve = pendingSendResolveRef.current;
                pendingSendResolveRef.current = null;
                resolve(false);
              }
            }
          }}
        />
      )}

      {/* Prepare modal – opened by "Verschlüsselung vorbereiten" button */}
      {showPrepareModal && prepareFlowResult && (
        <PasswordModal
          recipientSetKey={prepareFlowResult.recipientSetKey}
          decryptedEntries={prepareFlowResult.decryptedEntries}
          prefillPassword={prepareFlowResult.prefillPassword}
          existingEntryId={prepareFlowResult.existingEntryId}
          foundInPm={prepareFlowResult.foundInPm ?? false}
          isUnlocked={isUnlocked}
          passwortManagerAktiv={isActive}
          settings={settings}
          isPaid={isPaid}
          hasAttachments={prepareFlowResult.hasAttachments}
          allAttachmentsPdf={prepareFlowResult.allAttachmentsPdf}
          totalAttachmentBytes={prepareFlowResult.totalAttachmentBytes ?? 0}
          onPrepare={async ({ mode, password, keepSubjectUnencrypted }) => {
            await prepareEncryption({ password, mode, keepSubjectUnencrypted });
          }}
          onClose={(result) => {
            // Save/update password in PM after prepare
            if (
              result?.prepared &&
              result.usedPassword &&
              cryptoCtx.isUnlocked &&
              cryptoCtx.masterKey &&
              cryptoCtx.oid &&
              cryptoCtx.passwortDatenbankId &&
              prepareFlowResult?.recipientSetKey
            ) {
              encryptAndSave(
                cryptoCtx.oid,
                cryptoCtx.passwortDatenbankId,
                cryptoCtx.masterKey,
                prepareFlowResult.recipientSetKey,
                result.usedPassword,
                prepareFlowResult.existingEntryId ?? null,
              ).catch((err) =>
                console.error(
                  "[PasswordManager] PM save after prepare failed:",
                  err,
                ),
              );
            }
            setShowPrepareModal(false);
            setPrepareFlowResult(null);
            if (result?.prepared) {
              const duration = preparedInSeconds != null
                ? preparedInSeconds < 60
                  ? ` (${preparedInSeconds}s)`
                  : ` (${Math.floor(preparedInSeconds / 60)}m ${preparedInSeconds % 60}s)`
                : "";
              handleSuccess(`${t("Die E-Mail wurde für den verschlüsselten Versand vorbereitet.")}${duration}`);
            }
          }}
        />
      )}

      {/* Sidebar Unlock Overlay */}
      {showUnlock && (
        <SidebarUnlock
          onSuccess={() => setShowUnlock(false)}
          onCancel={() => setShowUnlock(false)}
        />
      )}

      {/* Dev-only: fixed bottom log panel */}
      {process.env.NODE_ENV === "development" && (
        <DevConsolePanel variant="sidebar" />
      )}
    </div>
  );
};

// Styles
const styles = {
  card: {
    backgroundColor: "#ffffff",
    padding: "16px 14px",
    paddingBottom: "16px",
    marginBottom: "4px",
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    boxShadow: "0 4px 16px rgba(100,100,100,0.18)",
  },
  cardTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "16px",
    marginTop: "0",
    color: "#333",
  },
  statusContainer: {
    marginBottom: "20px",
  },
  statusInactive: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeeba",
    color: "#856404",
    padding: "12px 16px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
  },
  statusLocked: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeeba",
    color: "#856404",
    padding: "12px 16px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
  },
  statusUnlocked: {
    backgroundColor: "#d4edda",
    border: "1px solid #c3e6cb",
    color: "#155724",
    padding: "12px 16px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
  },
  section: {
    marginBottom: "16px",
    paddingBottom: "0",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "12px",
    marginTop: "0",
    color: "#333",
  },
  button: {
    padding: "10px 10px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "100%",
    marginBottom: "12px",
  },
  buttonActivate: {
    backgroundColor: "#28a745",
    color: "#fff",
  },
  buttonUnlock: {
    backgroundColor: "#007bff",
    color: "#fff",
  },
  buttonManage: {
    backgroundColor: "#007bff",
    color: "#fff",
  },
  buttonDeactivate: {
    backgroundColor: "#ffc107",
    color: "#000",
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  description: {
    fontSize: "13px",
    color: "#666",
    lineHeight: "1.5",
    margin: "0",
  },
  descriptionSmall: {
    fontSize: "12px",
    color: "#888",
    lineHeight: "1.4",
    margin: "0",
    fontStyle: "italic",
  },
  warningBox: {
    backgroundColor: "#fff3cd",
    border: "1px solid #ffeeba",
    color: "#856404",
    padding: "12px 16px",
    borderRadius: "6px",
    fontSize: "13px",
    marginBottom: "16px",
    lineHeight: "1.5",
  },
  devSection: {
    marginTop: "24px",
    paddingTop: "16px",
    borderTop: "2px dashed #dc3545",
  },
  devLabel: {
    color: "#dc3545",
    fontSize: "12px",
    fontWeight: "600",
    margin: "0 0 8px 0",
  },
  buttonDev: {
    backgroundColor: "#dc3545",
    color: "#fff",
  },

  buttonVerschluesselnInaktiv: {
    backgroundColor: "#fff",
    color: "#495057",
    border: "2px solid #6c757d",
  },
  buttonVerschluesselnAktiv: {
    backgroundColor: "#28a745",
    color: "#fff",
    border: "2px solid #28a745",
  },
  buttonPrepare: {
    backgroundColor: "#6f42c1",
    color: "#fff",
  },
  buttonRestore: {
    backgroundColor: "#fd7e14",
    color: "#fff",
  },

  firstRunPanel: {
    backgroundColor: "#f6fbff",
    border: "1px solid #cfe5f7",
    borderRadius: "8px",
    padding: "14px",
    marginBottom: "16px",
  },
  firstRunHeader: {
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
    marginBottom: "10px",
  },
  firstRunIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "8px",
    backgroundColor: "#0078d7",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  firstRunTitle: {
    margin: "0 0 6px 0",
    fontSize: "16px",
    lineHeight: "1.25",
    color: "#12344d",
  },
  firstRunIntro: {
    margin: 0,
    fontSize: "13px",
    color: "#24445c",
    lineHeight: "1.5",
  },
  firstRunBenefit: {
    margin: "0 0 12px 0",
    fontSize: "13px",
    color: "#24445c",
    lineHeight: "1.5",
  },
  firstRunExamples: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "12px",
  },
  firstRunExampleItem: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "12px",
    color: "#24445c",
  },
  firstRunExampleIcon: {
    color: "#198754",
    flexShrink: 0,
  },
  firstRunButtons: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  firstRunPrimaryButton: {
    flex: 1,
    minWidth: "130px",
    padding: "8px",
    backgroundColor: "#0078d7",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
  },
  firstRunSecondaryButton: {
    flex: 1,
    minWidth: "130px",
    padding: "8px",
    backgroundColor: "#fff",
    color: "#24445c",
    border: "1px solid #9fc7e6",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "13px",
  },
};

export default PasswordManager;
